import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { LeaveAllocationService } from '../../services/leave-allocation.service';
import { LeaveTypeService } from '../../services/leave-type.service';
import { UserLeaveAllocation, LeaveTypeConfig } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { displayPersonName } from '../../utils/userUtils';
import { EmployeeAutocomplete } from '../common/EmployeeAutocomplete';

interface SimpleUser {
    id: string;
    username: string;
    fullName?: string;
}

const UserLeaveAllocationManagement: React.FC = () => {
    const [allocations, setAllocations] = useState<UserLeaveAllocation[]>([]);
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [fiscalYear, setFiscalYear] = useState<number>(new Date().getFullYear());
    const [selectedUser, setSelectedUser] = useState<SimpleUser | null>(null);
    const [leaveType, setLeaveType] = useState<string>('');
    const [numberOfLeaves, setNumberOfLeaves] = useState<number>(0);
    const toast = useToast();

    useEffect(() => {
        LeaveTypeService.getByInstitution(true).then((data) => {
            setLeaveTypes(data);
            if (data.length > 0) setLeaveType(data[0].name);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        fetchAllocations();
    }, [fiscalYear]);

    const fetchAllocations = async () => {
        try {
            setLoading(true);
            const data = await LeaveAllocationService.getAllocationsByInstitutionAndFiscalYear(fiscalYear);
            setAllocations(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch leave allocations');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrUpdate = async () => {
        if (!selectedUser) {
            toast.showError('Please select a user');
            return;
        }
        try {
            await LeaveAllocationService.createOrUpdate({
                institutionId: AuthService.getSelectedInstitution()!!,
                userId: selectedUser.id,
                leaveType,
                numberOfLeaves,
                fiscalYear
            });
            setDialogOpen(false);
            await fetchAllocations();
        } catch (err) {
            toast.showApiError(err, 'Failed to save leave allocation');
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this allocation override?')) {
            try {
                await LeaveAllocationService.deleteAllocation(id);
                await fetchAllocations();
            } catch (err) {
                toast.showApiError(err, 'Failed to delete allocation');
            }
        }
    };

    const handleEditClick = (allocation: UserLeaveAllocation) => {
        const preFilled: SimpleUser = { id: allocation.userId, username: allocation.userName, fullName: allocation.userDisplayName ?? undefined };
        setSelectedUser(preFilled);
        setLeaveType(allocation.leaveType);
        setNumberOfLeaves(allocation.numberOfLeaves);
        setDialogOpen(true);
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Box data-help-page="leave.allocations.page" sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>User Leave Allocations</Typography>

            <Box data-help-section="leave.allocations.filters" sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 3 }}>
                <TextField
                    data-help="leave.allocations.fiscalYear"
                    select
                    label="Fiscal Year"
                    value={fiscalYear}
                    onChange={(e) => setFiscalYear(Number(e.target.value))}
                    sx={{ minWidth: 150 }}
                    size="small"
                >
                    {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Button data-help="leave.allocations.add" variant="contained" onClick={() => {
                    setSelectedUser(null);
                    setLeaveType(leaveTypes[0]?.name ?? '');
                    setNumberOfLeaves(0);
                    setDialogOpen(true);
                }}>
                    Add Override
                </Button>
            </Box>

            <TableContainer data-help-section="leave.allocations.table" component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="leave.allocations.col.user">User</TableCell>
                            <TableCell data-help="leave.allocations.col.leaveType">Leave Type</TableCell>
                            <TableCell data-help="leave.allocations.col.days">Allocated Leaves</TableCell>
                            <TableCell data-help="leave.allocations.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allocations.map((alloc) => (
                            <TableRow key={alloc.id}>
                                <TableCell>{displayPersonName(alloc.userDisplayName, alloc.userName)}</TableCell>
                                <TableCell>{alloc.leaveType}</TableCell>
                                <TableCell>{alloc.numberOfLeaves}</TableCell>
                                <TableCell>
                                    <Button data-help="leave.allocations.action.edit" size="small" onClick={() => handleEditClick(alloc)}>Edit</Button>
                                    <IconButton data-help="leave.allocations.action.delete" size="small" color="error" onClick={() => handleDelete(alloc.id)} aria-label="delete">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {allocations.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">No allocation overrides for this fiscal year</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ 'data-help-page': 'leave.allocForm.page' } as any} maxWidth="sm" fullWidth>
                <DialogTitle>User Leave Allocation Override</DialogTitle>
                <DialogContent data-help-section="leave.allocForm.section.details">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <EmployeeAutocomplete<SimpleUser>
                            data-help="leave.allocForm.field.user"
                            label="User"
                            placeholder="Search users..."
                            size="medium"
                            fullWidth
                            value={selectedUser}
                            onChange={setSelectedUser}
                            onError={(err) => toast.showApiError(err, 'Failed to search users')}
                        />
                        <TextField
                            select
                            data-help="leave.allocForm.field.leaveType"
                            label="Leave Type"
                            value={leaveType}
                            onChange={(e) => setLeaveType(e.target.value)}
                            fullWidth
                        >
                            {leaveTypes.map((lt) => (
                                <MenuItem key={lt.id} value={lt.name}>{lt.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            data-help="leave.allocForm.field.days"
                            label="Number of Leaves"
                            type="number"
                            value={numberOfLeaves}
                            onChange={(e) => setNumberOfLeaves(Number(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button data-help="leave.allocForm.cancel" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button data-help="leave.allocForm.save" variant="contained" onClick={handleCreateOrUpdate}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserLeaveAllocationManagement;
